import { useEffect, useRef, useState } from 'react';
import { verifyScmToken } from '@/api/endpoints';
import type { ScmConnectionTestResult } from '@/api/schemas';
import { repositoryError, workspaceError, type ScmValues } from './scmConnectionModel';
import { verifyKey } from './tokenGuideModel';

/**
 * "Verify token" (plan P34): asks the API what the typed token can do in Bitbucket before anything is stored, on its own
 * once the token and what it reaches are known (once per combination), or on the button. The answer is shown only while
 * the kind, slugs and token it was asked for are unchanged.
 */
export function useVerifyScmToken(values: ScmValues) {
  const [answer, setAnswer] = useState<{ key: string; result: ScmConnectionTestResult } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const key = verifyKey(values);
  const ready = !workspaceError(values.workspace) && !repositoryError(values) && values.token.trim().length >= 20;

  const verify = async (): Promise<ScmConnectionTestResult> => {
    setBusy(true);
    setError(null);
    try {
      const result = await verifyScmToken({ workspace: values.workspace.trim(), ...(values.kind === 'repository' ? { repository: values.repository.trim() } : {}), token: values.token.trim() });
      setAnswer({ key, result });
      return result;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setBusy(false);
    }
  };
  const asked = useRef<string | null>(null);
  useEffect(() => {
    if (!ready || asked.current === key) return;
    const timer = setTimeout(() => {
      asked.current = key;
      void verify().catch(() => undefined);
    }, 300);
    return () => clearTimeout(timer);
    // `verify` reads the values of this render; the key says when they changed.
  }, [ready, key]);
  return { result: answer?.key === key ? answer.result : null, busy, error, ready, verify };
}

export type VerifyScmToken = ReturnType<typeof useVerifyScmToken>;
